/* eslint-disable no-unused-vars */
// File: frontend/src/components/dashboard/dashboardTokens.js
//
// Single source of truth for the dashboard's visual tokens, derived from the
// app's own LIGHT / DARK palette (frontend/src/theme.js). Reacts to the active
// palette mode (useTheme().palette.mode) so the dashboard follows the app's
// Light/Dark/System toggle.
//
// This pass adds a refined "glass" surface system (translucent cards + blur over
// a soft aurora backdrop) and raises muted-text contrast so labels, subtitles,
// legends and metadata stay clearly readable in BOTH modes.
//
// Colour language: emerald = success/primary · cyan/blue = network/system ·
// amber/gold = MONEY only · red/amber = warnings · slate/off-white = text.

// A SKIN, NOT A SECOND COLOUR SYSTEM. Every colour below is read from theme.js
// (LIGHT / DARK); this file only decides how the Router Dashboard wears them —
// the glass surfaces, the aurora, the depth. It holds no colour value of its
// own, which is what took it off the raw-hex lint exemption list. See the
// hierarchy written at the top of components/ui/tokens.js.

import { alpha } from "@mui/material/styles";
import { LIGHT, DARK } from "../../theme";

export function makeTokens(mode) {
  const isDark = mode === "dark";
  const p = isDark ? DARK : LIGHT;

  // MONEY. Amber/gold is reserved for money on this surface; the values live in
  // theme.js as `gold` so the rest of the product can use the same money tone.
  const gold       = p.gold.main;
  const goldBright = p.gold.bright;
  const goldText   = p.gold.text;
  const goldDim    = alpha(gold, isDark ? 0.14 : 0.10);
  const goldBdr    = alpha(gold, isDark ? 0.32 : 0.30);

  // Muted text. This used to be its own pair of values because the theme's
  // `text.disabled` failed contrast; that token now passes AA (see theme.js),
  // so the dashboard uses it rather than keeping a competing grey.
  const textMut = p.text.disabled;

  return {
    mode,
    isDark,

    // Solid surfaces (fallback / opaque needs)
    bg:         p.bg.page,
    surface:    p.bg.paper,
    surfaceUp:  p.bg.elevated,
    surfaceHov: isDark ? "rgba(255,255,255,0.05)" : p.bg.hover,
    sunken:     p.bg.sunken,
    border:     p.border.default,
    borderHov:  p.border.strong,

    // ── Glass system ────────────────────────────────────────────────────────
    glassBg:     isDark ? "rgba(13,17,23,0.64)"  : "rgba(255,255,255,0.70)",
    glassBgUp:   isDark ? "rgba(20,25,32,0.78)"  : "rgba(255,255,255,0.86)",
    glassBorder: isDark ? "rgba(255,255,255,0.10)" : "rgba(16,24,40,0.08)",
    glassInset:  isDark ? "inset 0 1px 0 rgba(255,255,255,0.05)" : "inset 0 1px 0 rgba(255,255,255,0.75)",
    glassBlur:   "saturate(135%) blur(14px)",
    // Soft fixed aurora behind the content so the blur has something to work on.
    pageBackdrop: isDark
      ? "radial-gradient(900px 520px at 10% -10%, rgba(74,222,128,0.10), transparent 60%), radial-gradient(820px 520px at 102% -4%, rgba(96,165,250,0.09), transparent 55%), radial-gradient(760px 620px at 50% 118%, rgba(240,180,41,0.05), transparent 60%)"
      : "radial-gradient(900px 520px at 10% -10%, rgba(5,150,105,0.10), transparent 60%), radial-gradient(820px 520px at 102% -4%, rgba(37,99,235,0.07), transparent 55%)",

    // Emerald (primary / success)
    green:    p.accent.main,
    greenText:p.accent.text,
    greenDim: p.accent.soft,
    greenBdr: p.accent.border,

    // Money (amber/gold)
    gold, goldBright, goldText, goldDim, goldBdr,

    // Warnings
    red:    p.error.main,
    redDim: p.error.light,
    redText:p.error.text || p.error.main,
    amber:  p.warning.main,
    amberDim:p.warning.light,
    amberText:p.warning.text || p.warning.main,

    // Network / system intelligence
    blue:    p.info.main,
    blueDim: p.info.light,
    blueText:p.info.text || p.info.main,

    // Text (raised contrast)
    textPri: p.text.primary,
    textSec: p.text.secondary,
    textMut,

    // Type
    fontDisplay: '"Inter","DM Sans",system-ui,sans-serif',
    fontMono:    'ui-monospace,"JetBrains Mono","Geist Mono",monospace',

    // Depth
    shadow: isDark
      ? "0 1px 2px rgba(0,0,0,0.45)"
      : "0 1px 2px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.05)",
    shadowUp: isDark
      ? "0 10px 30px rgba(0,0,0,0.55)"
      : "0 10px 30px rgba(16,24,40,0.12)",
    heroGradient: isDark
      ? "radial-gradient(120% 140% at 0% 0%, rgba(74,222,128,0.07), transparent 55%), radial-gradient(120% 140% at 100% 0%, rgba(96,165,250,0.05), transparent 55%)"
      : "radial-gradient(120% 140% at 0% 0%, rgba(5,150,105,0.05), transparent 55%), radial-gradient(120% 140% at 100% 0%, rgba(37,99,235,0.04), transparent 55%)",
  };
}

export default makeTokens;
